import { ChangeDetectorRef, Component, DestroyRef, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { finalize } from "rxjs/operators";
import { SweetAlertComponent } from "src/app/shared/SweetMesssage";
import {
  HorarioLaboral,
  HorarioLaboralDia,
} from "src/app/shared/model/tarea/horario-laboral.model";
import { HorarioLaboralService } from "../horario-laboral.service";
import {
  MENSAJE_TOLERANCIA_INVALIDA,
  TOLERANCIA_MAXIMA_MINUTOS,
  toleranciaParaApi,
  toleranciaValidator,
} from "./tolerancia-horario";

@Component({
  selector: "app-horario-editor",
  standalone: true,
  templateUrl: "./horario-editor.component.html",
  styleUrls: ["./horario-editor.component.css"],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
  ],
})
export class HorarioEditorComponent implements OnInit {
  form: FormGroup;
  loading = false;
  saving = false;
  horarioId: number | null = null;
  readonly toleranciaMaxima = TOLERANCIA_MAXIMA_MINUTOS;
  readonly mensajeToleranciaInvalida = MENSAJE_TOLERANCIA_INVALIDA;
  private destruido = false;
  readonly daysOfWeek = [
    { value: 1, label: "Lunes" },
    { value: 2, label: "Martes" },
    { value: 3, label: "Miércoles" },
    { value: 4, label: "Jueves" },
    { value: 5, label: "Viernes" },
    { value: 6, label: "Sábado" },
    { value: 7, label: "Domingo" },
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private horarioService: HorarioLaboralService,
    private sweetAlert: SweetAlertComponent,
    // En CosSolser la vista no se repinta sola tras una respuesta HTTP
    // (zone.js no parchea nada): sin esto el editor puede quedarse en
    // "Cargando horario" o con el botón en "Guardando..." hasta el
    // siguiente clic. El guardia evita pintar una vista ya destruida.
    private cdr: ChangeDetectorRef,
    destroyRef: DestroyRef
  ) {
    destroyRef.onDestroy(() => (this.destruido = true));
    this.form = this.fb.group({
      nombre: ["", [Validators.required, Validators.maxLength(120)]],
      descripcion: [""],
      estatus: ["ACTIVO", Validators.required],
      // Opcional: vacío = usa la tolerancia de Parámetros de asistencia.
      // Tiene que estar aquí porque el servidor escribe la columna con lo
      // que llegue: sin el campo, cada guardado desde el web borraba la
      // tolerancia capturada en el kiosco.
      minutosTolerancia: [null, toleranciaValidator],
      dias: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get("id");
    if (id) {
      this.horarioId = Number(id);
      this.cargarHorario();
      return;
    }
    this.addDia();
  }

  get dias(): FormArray {
    return this.form.get("dias") as FormArray;
  }

  bloquesComida(index: number): FormArray {
    return this.dias.at(index).get("bloquesComida") as FormArray;
  }

  addDia(dia?: HorarioLaboralDia): void {
    this.dias.push(
      this.fb.group({
        diaSemana: [dia?.diaSemana ?? null, Validators.required],
        horaEntrada: [this.normalizeTime(dia?.horaEntrada) ?? "08:00", Validators.required],
        horaSalida: [this.normalizeTime(dia?.horaSalida) ?? "17:00", Validators.required],
        minutosLaboralesObjetivo: [
          dia?.minutosLaboralesObjetivo ?? 480,
          [Validators.required, Validators.min(1)],
        ],
        estatus: [dia?.estatus ?? "ACTIVO", Validators.required],
        bloquesComida: this.fb.array(
          (dia?.bloquesComida ?? []).map((comida) =>
            this.fb.group({
              horaInicio: [this.normalizeTime(comida.horaInicio), Validators.required],
              horaFin: [this.normalizeTime(comida.horaFin), Validators.required],
            })
          )
        ),
      })
    );
  }

  removeDia(index: number): void {
    this.dias.removeAt(index);
  }

  addComida(index: number): void {
    this.bloquesComida(index).push(
      this.fb.group({
        horaInicio: ["14:00", Validators.required],
        horaFin: ["15:00", Validators.required],
      })
    );
  }

  removeComida(dayIndex: number, comidaIndex: number): void {
    this.bloquesComida(dayIndex).removeAt(comidaIndex);
  }

  guardar(): void {
    // La tolerancia va primero y con su propio mensaje (el del kiosco): el
    // genérico "Falta un campo requerido" no explica que el problema es
    // un 300 o un 7.5 en un campo opcional.
    const tolerancia = this.form.get("minutosTolerancia");
    if (tolerancia?.invalid) {
      tolerancia.markAsTouched();
      this.sweetAlert.showSwal("error-message-msg", MENSAJE_TOLERANCIA_INVALIDA);
      return;
    }

    if (this.form.invalid || this.dias.length === 0) {
      this.form.markAllAsTouched();
      this.sweetAlert.showSwal("info-message", null);
      return;
    }

    if (this.hasDuplicateDays()) {
      this.sweetAlert.showSwal("error-message-msg", "No se permiten días repetidos dentro del mismo horario.");
      return;
    }

    const horario = this.mapToHorario();
    this.saving = true;
    this.horarioService
      .guardarHorario(horario)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.refrescar();
        })
      )
      .subscribe({
        next: (response) => {
          if (response?.pCodRetorno === "00000") {
            this.sweetAlert.showSwal("success-message", null);
            this.router.navigate(["/TAREA/horarios"]);
            return;
          }
          this.sweetAlert.showSwal("error-message-msg", response?.pMensaje || "No se pudo guardar el horario.");
        },
        error: (error) => {
          // Un rechazo de negocio llega como Error con el texto del servidor
          // (ver HorarioLaboralService.guardarHorario); un fallo HTTP trae
          // un mensaje técnico que no sirve al usuario.
          const motivo =
            error instanceof HttpErrorResponse ? error.error?.pMensaje : error?.message;
          this.sweetAlert.showSwal("error-message-msg", motivo || "No se pudo guardar el horario.");
        },
      });
  }

  regresar(): void {
    this.router.navigate(["/TAREA/horarios"]);
  }

  trackByIndex(index: number): number {
    return index;
  }

  private cargarHorario(): void {
    if (!this.horarioId) {
      return;
    }

    this.loading = true;
    this.horarioService
      .obtenerHorario(this.horarioId)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.refrescar();
        })
      )
      .subscribe({
        next: (horario) => {
          if (horario?.pCodRetorno && horario.pCodRetorno !== "00000") {
            this.sweetAlert.showSwal("error-message-msg", horario.pMensaje);
            this.regresar();
            return;
          }
          // Un rechazo que se escapa como BusinessException llega como 200
          // con el cuerpo vacío (RestErrorHandler): sin esto el editor se
          // quedaba en "Editar horario" con el formulario en blanco, y
          // guardar pisaba el horario con la tolerancia en NULL sin que
          // nadie hubiera visto la guardada.
          if (horario?.id === null || horario?.id === undefined) {
            this.sweetAlert.showSwal("error-message-msg", "No se pudo cargar el horario solicitado.");
            this.regresar();
            return;
          }

          this.form.patchValue({
            nombre: horario?.nombre ?? "",
            descripcion: horario?.descripcion ?? "",
            estatus: horario?.estatus ?? "ACTIVO",
            // Se precarga tal cual: si llegara vacía, guardar sin tocarla
            // borraría la que se capturó en el kiosco.
            minutosTolerancia: horario?.minutosTolerancia ?? null,
          });
          this.dias.clear();
          (horario?.dias ?? []).forEach((dia) => this.addDia(dia));
          if (this.dias.length === 0) {
            this.addDia();
          }
        },
        error: () => {
          this.sweetAlert.showSwal("error-message-msg", "No se pudo cargar el horario solicitado.");
          this.regresar();
        },
      });
  }

  private mapToHorario(): HorarioLaboral {
    const raw = this.form.getRawValue();
    const horario = new HorarioLaboral();
    horario.id = this.horarioId ?? null;
    horario.nombre = raw.nombre?.trim();
    horario.descripcion = raw.descripcion?.trim() || null;
    horario.estatus = raw.estatus;
    horario.minutosTolerancia = toleranciaParaApi(raw.minutosTolerancia);
    horario.dias = raw.dias.map((dia: any) => ({
      diaSemana: Number(dia.diaSemana),
      horaEntrada: this.normalizeTimeForApi(dia.horaEntrada),
      horaSalida: this.normalizeTimeForApi(dia.horaSalida),
      minutosLaboralesObjetivo: Number(dia.minutosLaboralesObjetivo),
      estatus: dia.estatus,
      bloquesComida: (dia.bloquesComida ?? []).map((comida: any) => ({
        horaInicio: this.normalizeTimeForApi(comida.horaInicio),
        horaFin: this.normalizeTimeForApi(comida.horaFin),
      })),
    })) as any;
    return horario;
  }

  private hasDuplicateDays(): boolean {
    const values = this.dias.controls
      .map((control) => Number(control.get("diaSemana")?.value))
      .filter((value) => !Number.isNaN(value));
    return new Set(values).size !== values.length;
  }

  private normalizeTime(value: string | Date | null | undefined): string | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`;
    }
    const text = String(value);
    const match = text.match(/(\d{2}:\d{2})/);
    return match ? match[1] : text.slice(0, 5);
  }

  private normalizeTimeForApi(value: string): string {
    return value?.length === 5 ? `${value}:00` : value;
  }

  isFieldInvalid(control: AbstractControl | null): boolean {
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  /** markForCheck antes de detectChanges: ver el comentario del constructor. */
  private refrescar(): void {
    if (this.destruido) {
      return;
    }
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }
}
